import React from "react";
import { SIGNOL_COLOURS } from "@signolteam/shared/colours";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { AppUtils, SignolUtils } from "@src/utils";
import { Divider, DropDown, Loader, Tooltip } from "@src/components/atoms";
import BehaviourDescriptionSection from "@src/pages/sharedSections/BehaviourDescriptionSection";
import { signolImagesRepositoryURL } from "@src/constants/constants";
import { PlotlyGraph } from "@src/components/graphs";

const DataProcessingTab = () => {
  const { profile, selectedPeriod, selectedBehaviour, selectedTeam } =
    useManagerContext();
  const { getDynamicReplacement } = useDCContext();
  const auth = useAuthContext();

  const target = {
    behaviourType: selectedBehaviour.behaviourAcronym || "",
  };

  const acronym = selectedBehaviour.behaviourAcronym.toLowerCase();
  const customerCode = profile.customer.customerCode.toLowerCase();
  const {
    data: plotlyData,
    isPending: isPendingPlotlyData,
    error: errorPlotlyBVRData,
  } = ManagerHooks.useGetPlotlyBVRGraphs(
    [
      SignolUtils.normalizeBehiveKey(
        `prod/${customerCode}/figures/${acronym}/success/mm_exclusion_waffle_array.json`,
      ),
    ],
    auth.activeRole,
  );

  const {
    data: correctedData,
    isPending: isPendingCorrectedData,
    error: errorCorrectedData,
  } = ManagerHooks.useGetPlotlyBVRGraphs(
    [
      SignolUtils.normalizeBehiveKey(
        `prod/${customerCode}/data/${acronym}/success/model_description.json`,
      ),
    ],
    auth.activeRole,
  );

  const {
    data: { exclusions } = {},
    isPending: isPendingExclusions,
    error: errorExclusions,
  } = ManagerHooks.useGetBehaviourExclusions(auth.activeRole);

  const isMaritime = profile.customer.industry === "shipping";

  const state =
    isPendingPlotlyData || isPendingCorrectedData ? "loading" : "valid";

  return (
    <div className="h-full">
      {state === "loading" && <Loader size="2xl" />}

      {isPendingExclusions ? (
        <div>Loading...</div>
      ) : errorExclusions ? (
        <div className="text-red-500">Error fetching exclusions data...</div>
      ) : (
        selectedBehaviour != null && (
          <div className="px-8">
            <div className="px-4 mt-8">
              <div className="font-extrabold text-2xl pl-2">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__definition_section__title",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
              <div className="mt-2">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__definition_section__main_text",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
              <div className="mt-8">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__definition_section__figure_title",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
            </div>
            <BehaviourDescriptionSection
              target={target}
              exclusions={exclusions}
              behaviours={profile.behaviours}
              customer={profile.customer}
            />
          </div>
        )
      )}
      {(plotlyData?.graphs?.[0]?.content ||
        correctedData?.graphs?.[0]?.content) && <Divider padding={true} />}
      {state === "valid" && (
        <>
          {plotlyData?.graphs?.[0]?.content && (
            <section className="px-12">
              <div className="mt-8">
                <div className="font-extrabold text-2xl pl-2">
                  {getDynamicReplacement(
                    "fuel_manager__behaviour__data_processing__filters_section__title",
                    { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                  )}
                </div>
                <div className="mt-2">
                  {getDynamicReplacement(
                    "fuel_manager__behaviour__data_processing__filters_section__main_text",
                    { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                  )}
                </div>
                <div className="mt-8 mb-8">
                  {getDynamicReplacement(
                    "fuel_manager__behaviour__data_processing__filters_section__figure_title",
                    { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                  )}
                </div>
              </div>
              <PlotlyGraph
                plotyJSON={JSON.parse(plotlyData.graphs[0].content)[0].plotly}
              />
            </section>
          )}
          {plotlyData?.graphs?.[0]?.content &&
            correctedData?.graphs?.[0]?.content && <Divider padding={true} />}
          {correctedData?.graphs?.[0]?.content && (
            <section className="px-12">
              <div className="font-extrabold text-2xl pl-2">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__corrections_section__title",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
              <div className="mt-2">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__corrections_section__main_text",
                  {
                    BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
                    EXPLAINER_LINK: `<a className="text-primary-500 underline" target="_blank" href="${signolImagesRepositoryURL}/pdfs/econometric_explainer_${isMaritime ? "maritime" : "aviation"}.pdf">econometrics explainer</a>`,
                  },
                )}
              </div>
              <div className="mt-4 mb-4 font-bold">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__corrections_section__figure_title",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
              <ul className="px-8 list-disc">
                {JSON.parse(correctedData.graphs[0].content).map((obj) => (
                  <li key={obj.name}>
                    <span className="font-bold">{obj.display_name}</span>;{" "}
                    {obj.description}
                  </li>
                ))}
              </ul>
              <div className="mt-4 mb-16">
                {getDynamicReplacement(
                  "fuel_manager__behaviour__data_processing__corrections_section__footer_text",
                  { BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym },
                )}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default DataProcessingTab;
